// ============================================================
// MAIN APP — Router, Layout, Navigation
// ============================================================
import './styles.css';
import { icons } from './icons.js';
import { APP_NAME } from './config.js';

// ── State ────────────────────────────────────────────────
let currentPage = null;
let currentModule = null;

function getAdmin() {
  try { return JSON.parse(localStorage.getItem('admin_user')); } catch { return null; }
}

function isLoggedIn() { return !!localStorage.getItem('admin_token'); }

// ── Route definitions ────────────────────────────────────
const routes = [
  { path: '/login', module: () => import('./pages/login.js'), auth: false },
  { path: '/dashboard', module: () => import('./pages/dashboard.js'), auth: true },
  { path: '/register-business', module: () => import('./pages/register-business.js'), auth: true },
  { path: '/businesses', module: () => import('./pages/businesses.js'), auth: true },
  { path: '/business/:id', module: () => import('./pages/business-detail.js'), auth: true },
  { path: '/billing', module: () => import('./pages/billing.js'), auth: true },
  { path: '/referral-codes', module: () => import('./pages/referral-codes.js'), auth: true },
  { path: '/support', module: () => import('./pages/support.js'), auth: true },
  { path: '/notifications', module: () => import('./pages/notifications.js'), auth: true },
  { path: '/admin-users', module: () => import('./pages/admin-users.js'), auth: true, role: 'super_admin' },
];

const navItems = [
  { label: 'Overview', section: true },
  { path: '/dashboard', label: 'Dashboard', icon: 'dashboard' },
  { label: 'Businesses', section: true },
  { path: '/register-business', label: 'Register Business', icon: 'plus' },
  { path: '/businesses', label: 'All Businesses', icon: 'building' },
  { label: 'Operations', section: true },
  { path: '/billing', label: 'Billing', icon: 'creditCard' },
  { path: '/referral-codes', label: 'Referral Codes', icon: 'tag' },
  { label: 'Communication', section: true },
  { path: '/support', label: 'Support Inbox', icon: 'messageCircle' },
  { path: '/notifications', label: 'Notifications', icon: 'bell' },
  { label: 'Settings', section: true },
  { path: '/admin-users', label: 'Admin Users', icon: 'users', role: 'super_admin' },
];

// ── Hash router ──────────────────────────────────────────
function getHash() {
  return window.location.hash.slice(1) || '/dashboard';
}

function navigate(path) {
  window.location.hash = '#' + path;
}

function matchRoute(hash) {
  for (const route of routes) {
    const paramNames = [];
    const pattern = route.path.replace(/:(\w+)/g, (_, name) => {
      paramNames.push(name);
      return '([^/]+)';
    });
    const match = hash.match(new RegExp('^' + pattern + '$'));
    if (match) {
      const params = {};
      paramNames.forEach((name, i) => params[name] = match[i + 1]);
      return { route, params };
    }
  }
  return null;
}

// ── Layout rendering ─────────────────────────────────────
function renderSidebar() {
  const admin = getAdmin();
  const currentPath = getHash();
  const adminRole = admin?.role || 'staff';

  const navHtml = navItems
    .filter(item => !item.role || item.role === adminRole || adminRole === 'super_admin')
    .map(item => {
      if (item.section) return `<div class="sidebar__section">${item.label}</div>`;
      const isActive = currentPath === item.path || (item.path !== '/dashboard' && currentPath.startsWith(item.path));
      return `<a href="#${item.path}" class="nav-link ${isActive ? 'active' : ''}">${icons[item.icon] || ''}${item.label}</a>`;
    }).join('');

  const initials = admin?.full_name ? admin.full_name.split(' ').map(w => w[0]).join('').toUpperCase() : '?';

  return `
    <aside class="sidebar" id="sidebar">
      <div class="sidebar__brand" style="gap: 8px;">
        <div class="sidebar__logo" style="background: transparent; border: none; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; padding: 0;">
          ${icons.logo}
        </div>
        <div class="sidebar__name" style="font-family: var(--font-header); font-size: var(--fs-md); font-weight: 700; letter-spacing: -0.5px;">
          <span style="color: #ffffff; font-weight: 800;">Hukum</span>
        </div>
      </div>
      <nav class="sidebar__nav">${navHtml}</nav>
      <div class="sidebar__footer">
        <div class="sidebar__user">
          <div class="sidebar__avatar">${initials}</div>
          <div class="sidebar__user-info">
            <div class="sidebar__user-name">${admin?.full_name || 'Admin'}</div>
            <div class="sidebar__user-role">${admin?.role || 'staff'}</div>
          </div>
          <button onclick="window.__logout()" title="Logout" style="color:var(--text-tertiary)">${icons.logout}</button>
        </div>
      </div>
    </aside>`;
}

function renderHeader(title) {
  return `
    <header class="header">
      <div class="header__left">
        <button class="btn btn--icon" id="menu-toggle" style="display:none">${icons.menu}</button>
        <h1 class="header__title">${title}</h1>
      </div>
    </header>`;
}

// ── Page titles ──────────────────────────────────────────
const pageTitles = {
  '/dashboard': 'Dashboard',
  '/register-business': 'Register Business',
  '/businesses': 'All Businesses',
  '/billing': 'Billing Management',
  '/referral-codes': 'Referral Codes',
  '/support': 'Support Inbox',
  '/notifications': 'Notifications',
  '/admin-users': 'Admin Users',
};

// ── Main router ──────────────────────────────────────────
async function handleRoute() {
  const hash = getHash();
  const app = document.getElementById('app');

  // Cleanup previous page
  if (currentModule?.destroy) currentModule.destroy();

  // Auth check
  if (!isLoggedIn() && hash !== '/login') {
    navigate('/login');
    return;
  }
  if (isLoggedIn() && hash === '/login') {
    navigate('/dashboard');
    return;
  }

  // Find route
  const matched = matchRoute(hash);
  if (!matched) {
    navigate('/dashboard');
    return;
  }

  const { route, params } = matched;

  // Role check
  if (route.role) {
    const admin = getAdmin();
    if (admin?.role !== route.role && admin?.role !== 'super_admin') {
      navigate('/dashboard');
      return;
    }
  }

  // Login page (no layout)
  if (hash === '/login') {
    const mod = await route.module();
    app.innerHTML = mod.render();
    mod.init?.({ navigate });
    currentModule = mod;
    currentPage = hash;
    return;
  }

  // App layout
  const title = pageTitles[hash] || (hash.startsWith('/business/') ? 'Business Detail' : 'Admin Panel');
  
  app.innerHTML = `
    <div class="app-layout">
      ${renderSidebar()}
      <div class="main-area">
        ${renderHeader(title)}
        <div class="content fade-in" id="page-content">
          <div class="loading-page"><div class="spinner"></div></div>
        </div>
      </div>
    </div>`;

  // Bind menu toggle for mobile
  const menuToggle = document.getElementById('menu-toggle');
  const sidebar = document.getElementById('sidebar');
  menuToggle?.addEventListener('click', (e) => {
    e.stopPropagation();
    sidebar?.classList.toggle('open');
  });

  // Close sidebar when clicking outside or clicking any nav link
  document.addEventListener('click', (e) => {
    if (sidebar?.classList.contains('open') && !sidebar.contains(e.target) && e.target !== menuToggle) {
      sidebar.classList.remove('open');
    }
  });

  sidebar?.querySelectorAll('.nav-link').forEach(link => {
    link.addEventListener('click', () => {
      sidebar.classList.remove('open');
    });
  });

  // Load page module
  try {
    const mod = await route.module();
    const content = document.getElementById('page-content');
    content.innerHTML = mod.render(params);
    content.classList.remove('fade-in');
    void content.offsetWidth;
    content.classList.add('fade-in');
    mod.init?.(params);
    currentModule = mod;
    currentPage = hash;
  } catch (err) {
    console.error('Page load error:', err);
    document.getElementById('page-content').innerHTML = `
      <div class="empty">
        <div class="empty__title">Failed to load page</div>
        <p>${err.message}</p>
      </div>`;
  }
}

// ── Global functions ─────────────────────────────────────
window.__logout = () => {
  localStorage.removeItem('admin_token');
  localStorage.removeItem('admin_user');
  navigate('/login');
};

window.__navigate = navigate;

// ── Init ─────────────────────────────────────────────────
window.addEventListener('hashchange', handleRoute);
handleRoute();
